import {Wrapper} from "./components/wrapper.tsx"
import Logo from "@/assets/images/logo/logo-yeda-phone.svg";
import {Input} from "@/components/ui/input.tsx";
import {Button} from "@/components/ui/button.tsx";
import GoogleIcon from "@/assets/icons/brands/google.svg?react";
import {useForm} from "react-hook-form";
import z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from "@/components/ui/form.tsx";
import {PasswordInput} from "@/components/ui/password-input.tsx";
import {CheckCircleIcon} from "@heroicons/react/24/outline";
import {Link} from "react-router";
import {sendFormWithValidation} from "@/lib/utils.ts";
import {useResendVerificationEmail} from "@/hooks/useResendVerificationEmail.tsx";
import {useGuestPage} from "@/hooks/useGuestPage.ts";

const formSchema = z.object({
    email: z.string().email({
        message: 'אימייל לא תקין'
    }),
    password: z.string().min(8, {
        message: 'הסיסמא צריכה להכיל מינימום 8 תווים'
    }),
    password_confirm: z.string()
}).refine(data => data.password === data.password_confirm, {
    message: 'הסיסמאות אינן תואמות',
    path: ['password_confirm']
})

export type RegisterData = z.infer<typeof formSchema>

export default function RegisterPage() {

    const { register } = useGuestPage();

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: '',
            password: '',
            password_confirm: ''
        }
    });

    const {
        resendVerification,
        secondsToResend,
        reset
    } = useResendVerificationEmail();


    async function onSubmit(values: RegisterData) {
        await sendFormWithValidation<RegisterData>(register, values, form.setError)
            .then(() => reset())
    }

    if(form.formState.isSubmitSuccessful) {
        return (
            <Wrapper>
                <div className="flex justify-center items-center flex-col w-full gap-2">
                    <CheckCircleIcon className="w-20 h-20 stroke-1 text-lime-500"/>
                    <div className="text-3xl font-medium mb-4">מזל טוב!</div>
                    <div className="text-xl text-gray-500">החשבון שלך נוצר בהצלחה</div>
                    <div className="text-xl text-gray-500">שלחנו לך אימייל עם קישור לאימות החשבון</div>

                    <div className="mt-4 flex flex-col items-center gap-2 text-gray-400 text-xs">
                        <p>לא קיבלת את האימייל?</p>
                        <Button size="lg" disabled={secondsToResend !== null} onClick={resendVerification}>
                            שלח שוב {secondsToResend ? <span className="text-xs font-normal">{`(בעוד ${secondsToResend} שנ')`}</span> : ''}
                        </Button>
                    </div>
                </div>
            </Wrapper>
        )
    }

    return (
        <Wrapper>
            <div className="flex flex-col justify-center items-center w-full">
                <div className="flex w-96 flex-col gap-8">
                    <div className="flex justify-center h-8 w-8 overflow-hidden">
                        <img
                            alt="ידע פון"
                            src={Logo}
                            className="object-cover object-right"
                        />
                    </div>
                    <div>
                        <div className="text-2xl font-medium">פתיחת חשבון</div>
                    </div>
                    <Form {...form} >
                        <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>

                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <div className="flex-col flex gap-2">
                                            <FormLabel>אימייל</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <FormMessage />
                                            {form.formState.errors.email?.message?.[0] === 'האימייל כבר קיים במערכת' && (
                                                <Link
                                                    to="/login"
                                                    className="font-normal border-b border-dotted text-red-600 text-xs"
                                                >
                                                    היכנס כאן
                                                </Link>
                                            )}
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <div className="flex-col flex gap-2">
                                            <FormLabel>סיסמא</FormLabel>
                                            <FormControl>
                                                <PasswordInput {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="password_confirm"
                                render={({ field }) => (
                                    <FormItem>
                                        <div className="flex-col flex gap-2">
                                            <FormLabel>אימות סיסמא</FormLabel>
                                            <FormControl>
                                                <PasswordInput {...field} />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex justify-center items-center">
                                <Button
                                    disabled={form.formState.isSubmitting}
                                    type="submit"
                                    size="lg"
                                    className="w-full cursor-pointer"
                                >
                                    הרשמה
                                </Button>
                            </div>
                        </form>
                    </Form>
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-3 items-center text-gray-500">
                            <hr className="w-full border-gray-200"/>
                            <div>או</div>
                            <hr className="w-full border-gray-200"/>
                        </div>
                        <Button
                            variant="outline" size="lg">
                            <GoogleIcon/>
                            הירשם באמצעות גוגל
                        </Button>
                    </div>
                    <div className="flex gap-1 items-center">
                        <div className="text-sm text-gray-500">כבר יש לך חשבון?</div>
                        <Link className="text-sm text-gray-400 hover:text-gray-500 border-b border-dotted transition" to="/login">היכנס</Link>
                    </div>
                </div>
            </div>
            <div className="w-full bg-gray-100">

            </div>
        </Wrapper>
    )
}